import type { Chart, ChartEvent, Plugin } from 'chart.js';

import type { TimeSeriesChartDataset } from './build-time-series-chart-feedback-list-modal-options';
import {
  isSameTimeSeriesPeakPoint,
  type TimeSeriesPeakPoint,
} from './time-series-peak-point';

type ChartPointElement = {
  x?: number;
  y?: number;
  skip?: boolean;
};

type PeakPinTarget = {
  dataset: TimeSeriesChartDataset;
  dataIndex: number;
  dataLabel: string;
  element: {
    x: number;
    y: number;
  };
};

type CreateTimeSeriesPeakPinPluginParams = {
  peakPoints?: TimeSeriesPeakPoint[];
  resolvePeakPoints?: (chart: Chart<'line'>) => TimeSeriesPeakPoint[];
  onClick: (target: PeakPinTarget) => void;
};

const PIN_TIP_GAP = 2;
const PIN_RADIUS = 8;
const PIN_CENTER_OFFSET = 11;
const PIN_COLOR = '#ef4444';

const getPeakPinTargets = (
  chart: Chart<'line'>,
  peakPoints: TimeSeriesPeakPoint[] = [],
): PeakPinTarget[] => {
  if (peakPoints.length === 0) return [];

  const targets: PeakPinTarget[] = [];

  for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex += 1) {
    if (!chart.isDatasetVisible(datasetIndex)) continue;

    const dataset = chart.data.datasets[
      datasetIndex
    ] as TimeSeriesChartDataset;
    const rawData = dataset.data ?? [];

    for (let dataIndex = 0; dataIndex < rawData.length; dataIndex += 1) {
      const dataLabel =
        dataset.meta?.y?.[dataIndex] ??
        chart.data.labels?.[dataIndex] ??
        '';

      const isPeakPoint = peakPoints.some((peakPoint) =>
        isSameTimeSeriesPeakPoint(
          rawData[dataIndex],
          peakPoint,
          dataLabel,
          datasetIndex,
        ),
      );

      if (!isPeakPoint) {
        continue;
      }

      const element = chart.getDatasetMeta(datasetIndex).data[
        dataIndex
      ] as ChartPointElement;

      if (
        !element ||
        element.skip ||
        typeof element.x !== 'number' ||
        typeof element.y !== 'number'
      ) {
        continue;
      }

      targets.push({
        dataset,
        dataIndex,
        dataLabel: String(dataLabel),
        element: {
          x: element.x,
          y: element.y,
        },
      });
    }
  }

  return targets;
};

const drawPeakPin = (
  ctx: CanvasRenderingContext2D,
  element: PeakPinTarget['element'],
) => {
  const tipY = element.y - PIN_TIP_GAP;
  const centerY = tipY - PIN_CENTER_OFFSET;

  ctx.save();
  ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = PIN_COLOR;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;

  ctx.beginPath();
  ctx.moveTo(element.x, tipY);
  ctx.lineTo(element.x - 5, centerY + 5);
  ctx.lineTo(element.x + 5, centerY + 5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(element.x, centerY, PIN_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.shadowColor = 'transparent';
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(element.x, centerY, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const isInsidePeakPin = (
  event: ChartEvent,
  element: PeakPinTarget['element'],
) => {
  if (typeof event.x !== 'number' || typeof event.y !== 'number') return false;

  const tipY = element.y - PIN_TIP_GAP;
  const centerY = tipY - PIN_CENTER_OFFSET;

  return (
    Math.abs(event.x - element.x) <= PIN_RADIUS + 5 &&
    event.y >= centerY - PIN_RADIUS - 3 &&
    event.y <= tipY + 3
  );
};

export const createTimeSeriesPeakPinPlugin = ({
  peakPoints = [],
  resolvePeakPoints,
  onClick,
}: CreateTimeSeriesPeakPinPluginParams): Plugin<'line'> => {
  let isHoveringPin = false;
  const getPeakPoints = (chart: Chart<'line'>) =>
    resolvePeakPoints ? resolvePeakPoints(chart) : peakPoints;

  return {
    id: 'timeSeriesPeakPin',
    afterDatasetsDraw(chart) {
      getPeakPinTargets(chart, getPeakPoints(chart)).forEach((target) => {
        drawPeakPin(chart.ctx, target.element);
      });
    },
    afterEvent(chart, args) {
      const target = getPeakPinTargets(chart, getPeakPoints(chart)).find(
        (candidate) => isInsidePeakPin(args.event, candidate.element),
      );
      const isInsidePin = Boolean(target);

      if (args.event.type === 'click' && target) {
        onClick(target);
        args.changed = true;
        return;
      }

      if (isHoveringPin !== isInsidePin) {
        isHoveringPin = isInsidePin;
        chart.canvas.style.cursor = isInsidePin ? 'pointer' : '';
        args.changed = true;
      }
    },
  };
};
